import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Brain,
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Globe,
  Building2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react'
import {
  getAIModels,
  registerAIModel,
  updateAIModel,
  deleteAIModel,
  getAIModelEvaluations,
  createAIModelEvaluation,
  getRanches,
} from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { isSystemAdmin } from '../../lib/permissions'
import { format, parseISO } from 'date-fns'

// Mirror of mig 098 ai_model_runs_kind_chk + the route's ALLOWED_KINDS.
// Kept here so the dropdown stays self-documenting; if the backend allowlist
// grows, this list grows with it.
const MODEL_KINDS = [
  { value: 'vegetation_health_classifier', label: 'Vegetation health classifier' },
  { value: 'lantana_detector',              label: 'Lantana detector' },
  { value: 'biomass_estimator',             label: 'Biomass estimator' },
  { value: 'paddock_segmenter',             label: 'Paddock segmenter' },
  { value: 'cattle_counter',                label: 'Cattle counter' },
  { value: 'other',                         label: 'Other' },
]

function MetricPill({ label, value }) {
  if (value == null) return null
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
      <span className="text-gray-500">{label}</span>
      <span>{Number(value).toFixed(3)}</span>
    </span>
  )
}

function ScopeBadge({ scope }) {
  if (scope === 'global') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">
        <Globe className="h-3 w-3" />
        global
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
      <Building2 className="h-3 w-3" />
      per-ranch
    </span>
  )
}

function ActiveBadge({ isActive }) {
  return isActive ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
      <CheckCircle2 className="h-3 w-3" />
      active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      <XCircle className="h-3 w-3" />
      retired
    </span>
  )
}

function formatErrorDetail(err) {
  const detail = err?.response?.data?.detail
  if (Array.isArray(detail)) {
    return detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join('\n')
  }
  return detail || err?.message || 'Request failed'
}

function RegisterForm({ ranches, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    model_kind: 'vegetation_health_classifier',
    version: '',
    scope: 'global',
    ranch_id: '',
    description: '',
    notes: '',
    model_artifact_url: '',
  })
  const [error, setError] = useState(null)

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (data) => registerAIModel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiModels'] })
      onSuccess()
    },
    onError: (err) => setError(formatErrorDetail(err)),
  })

  const submit = (e) => {
    e.preventDefault()
    setError(null)
    const payload = {
      model_kind: form.model_kind,
      version: form.version,
      scope: form.scope,
      ranch_id: form.scope === 'per_ranch' ? form.ranch_id : null,
      description: form.description || null,
      notes: form.notes || null,
      model_artifact_url: form.model_artifact_url || null,
    }
    mutation.mutate(payload)
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
      <h3 className="font-semibold text-gray-900">Register new AI model</h3>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-sm whitespace-pre-line">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Model kind</span>
          <select
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.model_kind}
            onChange={(e) => setForm({ ...form, model_kind: e.target.value })}
          >
            {MODEL_KINDS.map(k => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-gray-700">Version</span>
          <input
            type="text"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            placeholder="e.g. 1.0"
            value={form.version}
            onChange={(e) => setForm({ ...form, version: e.target.value })}
            required
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-gray-700">Scope</span>
          <select
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.scope}
            onChange={(e) => setForm({ ...form, scope: e.target.value, ranch_id: '' })}
          >
            <option value="global">Global (serves every ranch)</option>
            <option value="per_ranch">Per-ranch (fine-tuned)</option>
          </select>
        </label>

        {form.scope === 'per_ranch' && (
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Ranch</span>
            <select
              className="mt-1 w-full rounded border-gray-300 text-sm"
              value={form.ranch_id}
              onChange={(e) => setForm({ ...form, ranch_id: e.target.value })}
              required
            >
              <option value="">— select —</option>
              {(ranches || []).map(r => (
                <option key={r.id} value={r.id}>{r.name} ({r.code})</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <label className="block">
        <span className="text-xs font-medium text-gray-700">Description</span>
        <textarea
          className="mt-1 w-full rounded border-gray-300 text-sm"
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </label>

      <label className="block">
        <span className="text-xs font-medium text-gray-700">Notes</span>
        <textarea
          className="mt-1 w-full rounded border-gray-300 text-sm"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </label>

      <label className="block">
        <span className="text-xs font-medium text-gray-700">Model artifact URL (optional)</span>
        <input
          type="url"
          className="mt-1 w-full rounded border-gray-300 text-sm"
          placeholder="gs://... or https://..."
          value={form.model_artifact_url}
          onChange={(e) => setForm({ ...form, model_artifact_url: e.target.value })}
        />
      </label>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded text-sm font-medium text-gray-700 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending || !form.version}
          className="px-3 py-1.5 rounded text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Registering...' : 'Register'}
        </button>
      </div>
    </form>
  )
}

function EditMetricsForm({ model, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    accuracy: model.accuracy ?? '',
    precision_score: model.precision_score ?? '',
    recall: model.recall ?? '',
    sample_size: model.sample_size ?? '',
    is_active: model.is_active,
    notes: model.notes ?? '',
  })
  const [error, setError] = useState(null)

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (data) => updateAIModel(model.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiModels'] })
      onSuccess()
    },
    onError: (err) => setError(formatErrorDetail(err)),
  })

  const submit = (e) => {
    e.preventDefault()
    setError(null)
    const payload = {}
    for (const k of ['accuracy', 'precision_score', 'recall']) {
      const v = form[k]
      if (v === '' || v === null) continue
      const n = Number(v)
      if (!Number.isNaN(n)) payload[k] = n
    }
    if (form.sample_size !== '' && form.sample_size !== null) {
      payload.sample_size = parseInt(form.sample_size, 10)
    }
    payload.is_active = form.is_active
    payload.notes = form.notes || null
    mutation.mutate(payload)
  }

  return (
    <form onSubmit={submit} className="bg-violet-50 rounded-lg border border-violet-200 p-4 space-y-3">
      <h4 className="font-semibold text-gray-900 text-sm">Edit metrics</h4>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-sm whitespace-pre-line">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Accuracy</span>
          <input
            type="number" step="0.001" min="0" max="1"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.accuracy}
            onChange={(e) => setForm({ ...form, accuracy: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Precision</span>
          <input
            type="number" step="0.001" min="0" max="1"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.precision_score}
            onChange={(e) => setForm({ ...form, precision_score: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Recall</span>
          <input
            type="number" step="0.001" min="0" max="1"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.recall}
            onChange={(e) => setForm({ ...form, recall: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Sample size</span>
          <input
            type="number" min="0"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.sample_size}
            onChange={(e) => setForm({ ...form, sample_size: e.target.value })}
          />
        </label>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
        />
        <span className="text-sm text-gray-700">Active (uncheck to retire without deleting)</span>
      </label>

      <label className="block">
        <span className="text-xs font-medium text-gray-700">Notes</span>
        <textarea
          className="mt-1 w-full rounded border-gray-300 text-sm"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </label>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded text-sm font-medium text-gray-700 hover:bg-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-3 py-1.5 rounded text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  )
}

function EvaluationForm({ modelId, ranches, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    ranch_id: '',
    evaluation_date: new Date().toISOString().slice(0, 10),
    accuracy: '',
    precision_score: '',
    recall: '',
    sample_size: '',
    notes: '',
  })
  const [error, setError] = useState(null)

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (data) => createAIModelEvaluation(modelId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiModelEvaluations', modelId] })
      onSuccess()
    },
    onError: (err) => setError(formatErrorDetail(err)),
  })

  const submit = (e) => {
    e.preventDefault()
    setError(null)
    if (!form.ranch_id) {
      setError('Ranch is required')
      return
    }
    const payload = {
      ranch_id: form.ranch_id,
      evaluation_date: form.evaluation_date,
    }
    for (const k of ['accuracy', 'precision_score', 'recall']) {
      if (form[k] !== '') payload[k] = Number(form[k])
    }
    if (form.sample_size !== '') payload.sample_size = parseInt(form.sample_size, 10)
    if (form.notes) payload.notes = form.notes
    mutation.mutate(payload)
  }

  return (
    <form onSubmit={submit} className="bg-amber-50 rounded-lg border border-amber-200 p-4 space-y-3">
      <h4 className="font-semibold text-gray-900 text-sm">Add per-ranch evaluation</h4>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-sm whitespace-pre-line">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Ranch</span>
          <select
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.ranch_id}
            onChange={(e) => setForm({ ...form, ranch_id: e.target.value })}
            required
          >
            <option value="">— select —</option>
            {(ranches || []).map(r => (
              <option key={r.id} value={r.id}>{r.name} ({r.code})</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Date</span>
          <input
            type="date"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.evaluation_date}
            onChange={(e) => setForm({ ...form, evaluation_date: e.target.value })}
            required
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Sample size</span>
          <input
            type="number" min="0"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.sample_size}
            onChange={(e) => setForm({ ...form, sample_size: e.target.value })}
          />
        </label>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Accuracy</span>
          <input
            type="number" step="0.001" min="0" max="1"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.accuracy}
            onChange={(e) => setForm({ ...form, accuracy: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Precision</span>
          <input
            type="number" step="0.001" min="0" max="1"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.precision_score}
            onChange={(e) => setForm({ ...form, precision_score: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-700">Recall</span>
          <input
            type="number" step="0.001" min="0" max="1"
            className="mt-1 w-full rounded border-gray-300 text-sm"
            value={form.recall}
            onChange={(e) => setForm({ ...form, recall: e.target.value })}
          />
        </label>
      </div>

      <label className="block">
        <span className="text-xs font-medium text-gray-700">Notes</span>
        <textarea
          className="mt-1 w-full rounded border-gray-300 text-sm"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </label>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded text-sm font-medium text-gray-700 hover:bg-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-3 py-1.5 rounded text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Add evaluation'}
        </button>
      </div>
    </form>
  )
}

function EvaluationsList({ modelId, ranchById }) {
  const { data: evals, isLoading } = useQuery({
    queryKey: ['aiModelEvaluations', modelId],
    queryFn: () => getAIModelEvaluations(modelId),
    enabled: !!modelId,
  })

  if (isLoading) return <p className="text-sm text-gray-500">Loading evaluations...</p>
  if (!evals || evals.length === 0) {
    return <p className="text-sm text-gray-500 italic">No evaluations yet.</p>
  }

  return (
    <ul className="space-y-2">
      {evals.map(ev => (
        <li key={ev.id} className="bg-gray-50 rounded p-2 text-sm">
          <div className="flex items-center justify-between">
            <div className="font-medium text-gray-700">
              {ranchById?.[ev.ranch_id]?.name || ev.ranch_id.slice(0, 8)}
            </div>
            <span className="text-xs text-gray-400">
              {ev.evaluation_date && format(parseISO(ev.evaluation_date), 'd MMM yyyy')}
            </span>
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            <MetricPill label="acc" value={ev.accuracy} />
            <MetricPill label="prec" value={ev.precision_score} />
            <MetricPill label="rec" value={ev.recall} />
            {ev.sample_size != null && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                <span className="text-gray-500">n=</span>{ev.sample_size}
              </span>
            )}
          </div>
          {ev.notes && <p className="text-xs text-gray-600 mt-1">{ev.notes}</p>}
        </li>
      ))}
    </ul>
  )
}

function ModelCard({ model, isExpanded, onToggle, ranches, ranchById, isAdmin }) {
  const [editMode, setEditMode] = useState(false)
  const [evalMode, setEvalMode] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const queryClient = useQueryClient()
  const deleteMutation = useMutation({
    mutationFn: () => deleteAIModel(model.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiModels'] })
      setConfirmDelete(false)
    },
  })

  const ranch = model.ranch_id ? ranchById?.[model.ranch_id] : null
  const noMetrics = model.accuracy == null && model.precision_score == null && model.recall == null

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Brain className="h-4 w-4 text-violet-600 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900">
              {MODEL_KINDS.find(k => k.value === model.model_kind)?.label || model.model_kind}
            </h3>
            <span className="font-mono text-xs text-gray-500">v{model.version}</span>
            <ScopeBadge scope={model.scope} />
            <ActiveBadge isActive={model.is_active} />
            {ranch && (
              <span className="text-xs text-gray-600">→ {ranch.name}</span>
            )}
          </div>
          {model.description && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{model.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-1 mt-2">
            <MetricPill label="acc" value={model.accuracy} />
            <MetricPill label="prec" value={model.precision_score} />
            <MetricPill label="rec" value={model.recall} />
            {model.sample_size != null && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                <span className="text-gray-500">n=</span>{model.sample_size}
              </span>
            )}
            {noMetrics && (
              <span className="text-xs text-gray-400 italic">no metrics yet</span>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            Registered {model.created_at && format(parseISO(model.created_at), 'd MMM yyyy')}
          </div>
        </div>
        <button
          onClick={onToggle}
          className="text-xs text-violet-600 hover:text-violet-700 hover:underline whitespace-nowrap flex items-center gap-1"
        >
          {isExpanded ? 'Hide' : 'View'}
          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
          {model.notes && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Notes</h4>
              <p className="text-sm text-gray-700 whitespace-pre-line">{model.notes}</p>
            </div>
          )}

          {model.model_artifact_url && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Artifact</h4>
              <a
                href={model.model_artifact_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-violet-600 hover:underline break-all"
              >
                {model.model_artifact_url}
              </a>
            </div>
          )}

          {model.f1_per_class && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Per-class F1</h4>
              <div className="flex flex-wrap gap-1">
                {Object.entries(model.f1_per_class).map(([cls, f1]) => (
                  <span
                    key={cls}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                  >
                    <span className="text-gray-500">{cls}</span>
                    <span>{Number(f1).toFixed(3)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setEditMode(!editMode)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium bg-violet-100 text-violet-700 hover:bg-violet-200"
              >
                <Edit2 className="h-3 w-3" />
                {editMode ? 'Cancel edit' : 'Edit metrics'}
              </button>
              <button
                onClick={() => setEvalMode(!evalMode)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium bg-amber-100 text-amber-700 hover:bg-amber-200"
              >
                <Plus className="h-3 w-3" />
                {evalMode ? 'Cancel eval' : 'Add evaluation'}
              </button>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 ml-auto"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              ) : (
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-sm text-red-700">Sure?</span>
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="px-3 py-1.5 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Yes, delete
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-1.5 rounded text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    No
                  </button>
                </div>
              )}
            </div>
          )}

          {editMode && isAdmin && (
            <EditMetricsForm
              model={model}
              onSuccess={() => setEditMode(false)}
              onCancel={() => setEditMode(false)}
            />
          )}
          {evalMode && isAdmin && (
            <EvaluationForm
              modelId={model.id}
              ranches={ranches}
              onSuccess={() => setEvalMode(false)}
              onCancel={() => setEvalMode(false)}
            />
          )}

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Per-ranch evaluations
            </h4>
            <EvaluationsList modelId={model.id} ranchById={ranchById} />
          </div>
        </div>
      )}
    </div>
  )
}

export default function AIModels() {
  const { role } = useAuth()
  const isAdmin = isSystemAdmin(role)
  const [showRegister, setShowRegister] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [filterKind, setFilterKind] = useState('')
  const [filterScope, setFilterScope] = useState('')

  const { data: models, isLoading, error } = useQuery({
    queryKey: ['aiModels', filterKind, filterScope],
    queryFn: () => getAIModels({
      ...(filterKind ? { model_kind: filterKind } : {}),
      ...(filterScope ? { scope: filterScope } : {}),
    }),
  })

  const { data: ranches } = useQuery({
    queryKey: ['ranches'],
    queryFn: () => getRanches(),
  })

  const ranchById = (ranches || []).reduce((acc, r) => {
    acc[r.id] = r
    return acc
  }, {})

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="h-6 w-6 text-violet-600" />
            AI Model Registry
          </h1>
          <p className="text-gray-500 mt-1">
            Platform-level classifier registry. CAL §3.4(g) accuracy reporting.
          </p>
        </div>
        {isAdmin && !showRegister && (
          <button
            onClick={() => setShowRegister(true)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700"
          >
            <Plus className="h-4 w-4" />
            Register model
          </button>
        )}
      </div>

      {showRegister && isAdmin && (
        <RegisterForm
          ranches={ranches}
          onSuccess={() => setShowRegister(false)}
          onCancel={() => setShowRegister(false)}
        />
      )}

      <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-gray-200 p-3">
        <span className="text-xs font-medium text-gray-600">Filter:</span>
        <select
          className="rounded border-gray-300 text-sm"
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value)}
        >
          <option value="">All kinds</option>
          {MODEL_KINDS.map(k => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
        <select
          className="rounded border-gray-300 text-sm"
          value={filterScope}
          onChange={(e) => setFilterScope(e.target.value)}
        >
          <option value="">All scopes</option>
          <option value="global">Global</option>
          <option value="per_ranch">Per-ranch</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Failed to load AI models</p>
            <p className="text-sm mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {isLoading && <p className="text-gray-500">Loading models...</p>}

      {!isLoading && models && models.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Brain className="h-10 w-10 text-gray-400 mx-auto mb-2" />
          <h3 className="font-semibold text-gray-900">No AI models registered</h3>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin
              ? 'Click "Register model" to register the first one.'
              : 'A system admin can register models here.'}
          </p>
        </div>
      )}

      {!isLoading && models && models.length > 0 && (
        <div className="space-y-3">
          {models.map(m => (
            <ModelCard
              key={m.id}
              model={m}
              isExpanded={expandedId === m.id}
              onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
              ranches={ranches}
              ranchById={ranchById}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  )
}
